import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import type { FlowInstanceEventNotification } from '@@components';

import { FlowClient, FlowInstanceStatus } from '../../../api/api';
import { useApiClient } from '@@common';
import { ExecutionTraceStep } from '../Services/FlowExecutionTraceService';
import { getFlowInstanceLiveState } from '../Services/getFlowInstanceLiveState';

const REFRESH_INTERVAL_MS = 3000;
type TrackingBaseline = {
	definitionId?: string;
	captured: boolean;
	instanceId: string | null;
	createdDate?: string;
};

export function useTrackedFlowRun(definitionId: string | undefined, enabled: boolean, events: FlowInstanceEventNotification[]) {
	const client = useApiClient(FlowClient);
	const latestDefinitionEvent = events.find((event) => event.payload.definitionId === definitionId);
	const { data: latestInstance, isFetched, refetch: refetchLatestInstance } = useQuery({
		queryKey: ['flow', 'tracked-latest-instance', definitionId],
		queryFn: async () => {
			const result = await client.searchInstance(undefined, definitionId, undefined, undefined, undefined, undefined, undefined, 0, 1, 'CreatedDate', false);
			return result.items[0];
		},
		enabled: enabled && !!definitionId,
		refetchInterval: enabled ? REFRESH_INTERVAL_MS : false,
	});
	const [trackingBaseline, setTrackingBaseline] = useState<TrackingBaseline>({ captured: false, instanceId: null });

	useEffect(() => {
		if (!enabled || !definitionId) {
			setTrackingBaseline((current) => current.definitionId === undefined && !current.captured ? current : { captured: false, instanceId: null });
			return;
		}

		if (trackingBaseline.definitionId !== definitionId) {
			setTrackingBaseline({ definitionId, captured: false, instanceId: null });
			return;
		}

		if (!trackingBaseline.captured && isFetched) {
			setTrackingBaseline({
				definitionId,
				captured: true,
				// Join an existing active run; only completed history belongs to the baseline.
				instanceId: latestInstance?.status === FlowInstanceStatus.Running || latestInstance?.status === FlowInstanceStatus.Waiting
					? null : latestInstance?.id ?? null,
				createdDate: latestInstance?.createdDate,
			});
		}
	}, [definitionId, enabled, isFetched, latestInstance?.createdDate, latestInstance?.id, latestInstance?.status, trackingBaseline]);

	useEffect(() => {
		if (enabled && latestDefinitionEvent) {
			void refetchLatestInstance();
		}
	}, [enabled, latestDefinitionEvent, refetchLatestInstance]);

	const baselineMatches = trackingBaseline.definitionId === definitionId && trackingBaseline.captured;
	const latestInstanceIsNew = latestInstance && baselineMatches &&
		(latestInstance.id !== trackingBaseline.instanceId &&
			(!trackingBaseline.createdDate || new Date(latestInstance.createdDate).getTime() >= new Date(trackingBaseline.createdDate).getTime()));
	const trackedInstance = getFlowInstanceLiveState(enabled && latestInstanceIsNew ? latestInstance : undefined, events);
	const instanceId = trackedInstance?.id;
	const latestInstanceEvent = events.find((event) => event.payload.id === instanceId);
	const { data: runs, refetch: refetchRuns } = useQuery({
		queryKey: ['flow', 'tracked-instance-runs', instanceId],
		queryFn: () => client.getInstanceRuns(instanceId!),
		enabled: !!instanceId,
		refetchInterval: instanceId && (trackedInstance?.status === FlowInstanceStatus.Running || trackedInstance?.status === FlowInstanceStatus.Waiting)
			? REFRESH_INTERVAL_MS : false,
	});

	useEffect(() => {
		if (latestInstanceEvent) {
			void refetchRuns();
		}
	}, [latestInstanceEvent, refetchRuns]);

	useEffect(() => {
		if (!instanceId) {
			return;
		}
		const status = (latestInstanceEvent?.payload.status as FlowInstanceStatus | undefined) ?? trackedInstance?.status;
		if (status !== FlowInstanceStatus.Completed && status !== FlowInstanceStatus.Failed && status !== FlowInstanceStatus.Canceled) {
			return;
		}
		const timer = setTimeout(() => void refetchRuns(), REFRESH_INTERVAL_MS);
		return () => clearTimeout(timer);
	}, [instanceId, trackedInstance?.status, latestInstanceEvent, refetchRuns]);

	const steps = useMemo<ExecutionTraceStep[]>(() => {
		const latestRun = runs?.reduce((latest, run) => !latest || new Date(run.createdDate) > new Date(latest.createdDate) ? run : latest, undefined as (typeof runs)[number] | undefined);
		if (!latestRun || (latestInstanceEvent?.payload.runId && latestRun.runId !== latestInstanceEvent.payload.runId)) {
			return [];
		}
		return (latestRun.steps ?? []).map((step) => ({ ...step, runId: latestRun.runId }));
	}, [runs, latestInstanceEvent]);
	return {
		instanceId,
		steps,
		status: trackedInstance?.status,
		currentActivityId: trackedInstance?.currentActivityId,
	};
}
